import React from 'react';
import { LOCATIONS_WITH_COORDS, DEBUG_MODE } from './constants';
import { getProgramType, getProgramColor, mapDaysToNames, formatTimeRange } from './helpers';

const Step4ManualSelection = React.memo(function Step4ManualSelection({
  userType,
  newPatientData,
  existingPatientResult,
  assignedProvider,
  assignedLocation,
  selectedLocation,
  setSelectedLocation,
  setProviders,
  setSelectedProvider,
  setAvailableTimeSlots,
  setSelectedTimeSlot,
  loadProvidersForStep3,
  selectedVisitDate,
  setSelectedVisitDate,
  providers,
  selectedProvider,
  bookingData,
  setBookingData,
  loadTimeSlots,
  availableTimeSlots,
  isLoadingTimeSlots,
  selectedTimeSlot,
  debugMode,
  visitTypes,
  selectedVisitType,
  handleNextStep,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Own Appointment</h2>
        <p className="text-gray-600">Choose your preferred location, date, and provider</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center text-blue-900">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-semibold">
            {userType === 'new'
              ? `${newPatientData.first_name} ${newPatientData.last_name}`
              : `${existingPatientResult?.FirstName} ${existingPatientResult?.LastName}`}
          </span>
        </div>
      </div>

      {userType === 'existing' && assignedProvider && assignedLocation && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-semibold text-orange-900 mb-1">Assigned Provider &amp; Location</h4>
              <p className="text-sm text-orange-800 mb-2">
                As an existing patient, you can only schedule with your assigned provider and location:
              </p>
              <div className="text-sm text-orange-900 bg-white rounded p-2">
                <div><strong>Provider:</strong> {assignedProvider.name}</div>
                <div><strong>Location:</strong> {assignedLocation.name}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-center space-x-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <span className="text-sm text-orange-700">
              <strong>Available Providers:</strong> Showing PA, PA-C, PMHNP, and NP providers available for your selected date and location.
            </span>
          </div>

          {/* Date Selection — comes first so location load always has a valid date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. Preferred Visit Date *
            </label>
            <input
              type="date"
              value={selectedVisitDate}
              onChange={(e) => {
                setSelectedVisitDate(e.target.value);
                setAvailableTimeSlots([]);
                setSelectedTimeSlot('');
                if (selectedLocation) loadProvidersForStep3(selectedLocation, e.target.value);
                if (selectedProvider && e.target.value) {
                  const provider = providers.find(p => p.ResourceId.toString() === selectedProvider);
                  if (provider) loadTimeSlots(selectedProvider, provider.ScheduleSetupID, e.target.value);
                }
              }}
              min={new Date().toISOString().split('T')[0]}
              max={(() => {
                const maxDate = new Date();
                maxDate.setDate(maxDate.getDate() + 90);
                return maxDate.toISOString().split('T')[0];
              })()}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
            {!selectedVisitDate && (
              <p className="mt-1 text-xs text-gray-500">Select a date first — providers will load automatically after you choose a location.</p>
            )}
          </div>

          {/* Location Selection — loads providers for the selected date above */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              2. Select Location *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(() => {
                let locationsToShow = Object.values(LOCATIONS_WITH_COORDS);
                if (userType === 'existing' && assignedLocation) {
                  const assignedLoc = LOCATIONS_WITH_COORDS[assignedLocation.id];
                  if (assignedLoc) locationsToShow = [assignedLoc];
                }
                return locationsToShow.map(location => {
                  const isSelected = selectedLocation === location.id;
                  const showDistance = location.distance !== undefined && location.distance > 0;
                  return (
                    <button
                      key={location.id}
                      type="button"
                      disabled={!selectedVisitDate}
                      onClick={() => {
                        setSelectedLocation(location.id);
                        setProviders([]);
                        setSelectedProvider('');
                        setAvailableTimeSlots([]);
                        setSelectedTimeSlot('');
                        const fallbackDate = selectedVisitDate || new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        loadProvidersForStep3(location.id, fallbackDate);
                      }}
                      className={`p-4 rounded-xl border-2 transition-all shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed ${
                        isSelected
                          ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-300'
                          : 'border-gray-300 bg-white hover:border-orange-300 hover:bg-orange-50/50'
                      }`}
                    >
                      <div className="text-center">
                        <svg
                          className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-orange-600' : 'text-gray-500'}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          {location.isVirtual ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          )}
                        </svg>
                        <div className={`font-semibold text-sm mb-1 ${isSelected ? 'text-orange-900' : 'text-gray-900'}`}>
                          {location.name.replace(' - NAMHS', '')}
                        </div>
                        {showDistance && (
                          <div className="text-xs text-gray-600">{location.distance.toFixed(1)} mi away</div>
                        )}
                        {location.isVirtual && (
                          <div className="text-xs text-blue-600 font-medium mt-1">Virtual/Online</div>
                        )}
                        {isSelected && (
                          <div className="mt-2">
                            <svg className="w-5 h-5 text-orange-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Provider Selection */}
          {providers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                3. Select Provider * ({(() => {
                  if (userType === 'existing' && assignedProvider) {
                    return providers.filter(p => p.ResourceId.toString() === assignedProvider.id).length;
                  }
                  return providers.length;
                })()} available)
              </label>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {(() => {
                  let providersToShow = providers;
                  if (userType === 'existing' && assignedProvider) {
                    providersToShow = providers.filter(p => p.ResourceId.toString() === assignedProvider.id);
                    if (providersToShow.length === 0 && DEBUG_MODE) {
                      console.warn('⚠️ Assigned provider not found in provider list:', assignedProvider);
                    }
                  }
                  return providersToShow.map((provider) => {
                    const programType = getProgramType(provider.ProviderName);
                    const isSelected = selectedProvider === provider.ResourceId.toString();
                    return (
                      <div
                        key={provider.ResourceId}
                        className={`border-l-4 pl-4 pr-4 py-3 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50 shadow-md'
                            : 'border-gray-300 bg-white hover:border-orange-300 hover:bg-orange-50/50 shadow-sm'
                        }`}
                        onClick={() => {
                          setAvailableTimeSlots([]);
                          setSelectedTimeSlot('');
                          setSelectedProvider(provider.ResourceId.toString());
                          setBookingData(prev => ({
                            ...prev,
                            facility_id: parseInt(selectedLocation),
                            resource_id: provider.ResourceId,
                            schedule_setup_id: provider.ScheduleSetupID,
                            schedule_id: provider.ScheduleID || null
                          }));
                          if (selectedVisitDate) {
                            loadTimeSlots(provider.ResourceId.toString(), provider.ScheduleSetupID, selectedVisitDate);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="font-semibold text-gray-900">{provider.ProviderName}</span>
                            </div>
                            <div className="ml-7 space-y-1">
                              <div className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getProgramColor(programType)}`}>
                                {programType}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Available: {mapDaysToNames(provider.DayOfTheWeek)}
                              </div>
                              {debugMode && (
                                <div className="text-xs text-gray-500 mt-1">
                                  ID: {provider.ResourceId} | Setup: {provider.ScheduleSetupID}
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="ml-2">
                              <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Time Slot Selection */}
          {selectedProvider && availableTimeSlots.length > 0 && (
            <div className="bg-white rounded-lg border-2 border-orange-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                4. Select Time Slot * ({availableTimeSlots.filter(slot => slot.IsAvailable === 1 && !slot.IsDisabled).length} available)
              </label>
              {(() => {
                const availableSlots = availableTimeSlots.filter(slot => slot.IsAvailable === 1 && !slot.IsDisabled);
                const morningSlots = availableSlots.filter(slot => {
                  const hour = parseInt(slot.Time.split(':')[0]);
                  const isPM = slot.Time.includes('PM');
                  return (!isPM && hour !== 12) || (hour === 12 && slot.Time.includes('AM'));
                });
                const afternoonSlots = availableSlots.filter(slot => {
                  const hour = parseInt(slot.Time.split(':')[0]);
                  const isPM = slot.Time.includes('PM');
                  return (isPM && hour !== 12) || (hour === 12 && isPM);
                });
                return (
                  <div className="space-y-4">
                    {morningSlots.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-1 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          Morning ({morningSlots.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {morningSlots.map(slot => {
                            const timeRange = formatTimeRange(slot.Time, slot.Duration);
                            const isSelected = selectedTimeSlot === slot.Time;
                            return (
                              <button
                                key={slot.ScheduleID}
                                type="button"
                                className={`px-4 py-3 rounded-lg border-2 cursor-pointer transition-all font-medium text-sm shadow-sm hover:shadow ${
                                  isSelected
                                    ? 'border-orange-500 bg-orange-100 text-orange-800 ring-2 ring-orange-300'
                                    : 'border-orange-300 bg-white text-orange-700 hover:bg-orange-50 hover:border-orange-500'
                                }`}
                                onClick={() => {
                                  setSelectedTimeSlot(slot.Time);
                                  let formattedDate = selectedVisitDate;
                                  if (selectedVisitDate && selectedVisitDate.includes('-')) {
                                    const dateParts = selectedVisitDate.split('-');
                                    formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                                  }
                                  setBookingData(prev => ({
                                    ...prev,
                                    preferred_date: formattedDate,
                                    preferred_time: slot.Time,
                                    schedule_id: slot.ScheduleID,
                                    duration: slot.Duration
                                  }));
                                  setTimeout(() => {
                                    const el = document.querySelector('.appointment-confirmation-box');
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }, 100);
                                }}
                              >
                                <div className="text-center">
                                  <div className="font-semibold">{timeRange.displayRange}</div>
                                  <div className="text-xs opacity-75">({slot.Duration} min)</div>
                                  {isSelected && <div className="text-[10px] font-bold mt-1">✓ Selected</div>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {afternoonSlots.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                          </svg>
                          Afternoon ({afternoonSlots.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {afternoonSlots.map(slot => {
                            const timeRange = formatTimeRange(slot.Time, slot.Duration);
                            const isSelected = selectedTimeSlot === slot.Time;
                            return (
                              <button
                                key={slot.ScheduleID}
                                type="button"
                                className={`px-4 py-3 rounded-lg border-2 cursor-pointer transition-all font-medium text-sm shadow-sm hover:shadow ${
                                  isSelected
                                    ? 'border-orange-500 bg-orange-100 text-orange-800 ring-2 ring-orange-300'
                                    : 'border-orange-300 bg-white text-orange-700 hover:bg-orange-50 hover:border-orange-500'
                                }`}
                                onClick={() => {
                                  setSelectedTimeSlot(slot.Time);
                                  let formattedDate = selectedVisitDate;
                                  if (selectedVisitDate && selectedVisitDate.includes('-')) {
                                    const dateParts = selectedVisitDate.split('-');
                                    formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                                  }
                                  setBookingData(prev => ({
                                    ...prev,
                                    preferred_date: formattedDate,
                                    preferred_time: slot.Time,
                                    schedule_id: slot.ScheduleID,
                                    duration: slot.Duration
                                  }));
                                  setTimeout(() => {
                                    const el = document.querySelector('.appointment-confirmation-box');
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }, 100);
                                }}
                              >
                                <div className="text-center">
                                  <div className="font-semibold">{timeRange.displayRange}</div>
                                  <div className="text-xs opacity-75">({slot.Duration} min)</div>
                                  {isSelected && <div className="text-[10px] font-bold mt-1">✓ Selected</div>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedProvider && selectedVisitDate && (availableTimeSlots.length === 0 || availableTimeSlots.filter(slot => slot.IsAvailable === 1 && !slot.IsDisabled).length === 0) && !isLoadingTimeSlots && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="text-orange-500 mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-orange-800 mb-1">Provider Not Available</h4>
                  <p className="text-orange-700 text-sm">
                    {providers.find(p => p.ResourceId.toString() === selectedProvider)?.ProviderName} is not available on {(() => {
                      const [year, month, day] = selectedVisitDate.split('-');
                      const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                    })()}.
                  </p>
                  <p className="text-orange-600 text-sm mt-2">
                    Please try selecting a different date or choose another provider.
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedProvider && selectedVisitDate && isLoadingTimeSlots && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-orange-700">Loading available time slots...</span>
              </div>
            </div>
          )}

          {debugMode && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg">
              <details className="group">
                <summary className="cursor-pointer p-4 hover:bg-orange-100 transition-colors">
                  <h4 className="font-semibold text-orange-800 inline-flex items-center">
                    🔍 Debug: Provider Selection Data
                    <svg className="w-4 h-4 ml-2 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </h4>
                </summary>
                <div className="px-4 pb-4">
                  <pre className="text-xs text-orange-700 overflow-x-auto">
                    {JSON.stringify({
                      selectedLocation,
                      selectedProvider,
                      selectedVisitDate,
                      selectedTimeSlot,
                      selectedVisitType,
                      visitTypeName: visitTypes.find(vt => vt.id.toString() === selectedVisitType)?.name,
                      providersCount: providers.length,
                      availableTimeSlotsCount: availableTimeSlots.length,
                      bookingData
                    }, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleNextStep}
          disabled={!selectedLocation || !selectedProvider || !selectedTimeSlot}
          className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Review Appointment
        </button>
      </div>
    </div>
  );
});

export default Step4ManualSelection;
